<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ThinkingOrb from 'dashboard/components-next/orb/ThinkingOrb.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['retry']);

const messageContainer = ref(null);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isUserMessage = sender => sender === 'user';

const getMessageAlignment = sender =>
  isUserMessage(sender) ? 'justify-end' : 'justify-start';

const getMessageDirection = sender =>
  isUserMessage(sender) ? 'flex-row-reverse' : 'flex-row';

const getAvatarName = sender =>
  isUserMessage(sender)
    ? t('CAPTAIN.PLAYGROUND.USER')
    : t('CAPTAIN.PLAYGROUND.ASSISTANT');

const getMessageStyle = sender =>
  isUserMessage(sender)
    ? 'bg-n-solid-blue text-n-slate-12 rounded-br-sm rounded-bl-xl rounded-t-xl'
    : 'bg-n-solid-iris text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl';

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => props.messages.length, scrollToBottom);
</script>

<template>
  <div
    ref="messageContainer"
    class="flex-1 overflow-y-auto mb-4 px-6 space-y-6"
  >
    <div
      v-for="(message, index) in messages"
      :key="index"
      class="flex"
      :class="getMessageAlignment(message.sender)"
    >
      <div
        class="flex items-end gap-1.5 max-w-[90%] md:max-w-[60%]"
        :class="getMessageDirection(message.sender)"
      >
        <Avatar
          :name="getAvatarName(message.sender)"
          rounded-full
          :size="24"
          class="shrink-0"
        />
        <div
          class="flex flex-col gap-1.5 min-w-0"
          :class="isUserMessage(message.sender) ? 'items-end' : 'items-start'"
        >
          <!-- bolha de erro local (falha na chamada) com ação de repetir -->
          <div
            v-if="message.type === 'error'"
            class="px-4 py-3 text-sm rounded-bl-sm rounded-br-xl rounded-t-xl bg-n-ruby-3 text-n-ruby-11 flex flex-col items-start gap-2"
          >
            <span>{{ message.content }}</span>
            <NextButton
              sm
              ruby
              link
              label="Tentar novamente"
              icon="i-ri-anticlockwise-line"
              class="hover:!no-underline"
              @click="emit('retry', index)"
            />
          </div>
          <!-- bolha de estado: a IA transferiria pra um humano -->
          <div
            v-else-if="message.type === 'handoff'"
            class="px-4 py-3 text-sm rounded-bl-sm rounded-br-xl rounded-t-xl bg-n-slate-3 text-n-slate-11 italic flex items-center gap-1.5"
          >
            <span class="i-ri-arrow-left-right-line size-3.5 shrink-0" />
            A IA transferiria esta conversa para um atendente humano.
          </div>
          <div
            v-else
            class="px-4 py-3 text-sm [overflow-wrap:break-word]"
            :class="getMessageStyle(message.sender)"
          >
            <div v-html="formatMessage(message.content)" />
          </div>
          <!-- tools usadas nesta resposta (trace do backend) -->
          <div
            v-if="message.toolCalls?.length"
            class="flex flex-wrap gap-1 max-w-full"
          >
            <span
              v-for="(tool, toolIndex) in message.toolCalls"
              :key="`${tool}-${toolIndex}`"
              class="px-1.5 py-0.5 text-[10px] font-mono rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              {{ tool }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isLoading" class="flex justify-start">
      <div class="flex items-start gap-1.5">
        <Avatar :name="getAvatarName('assistant')" rounded-full :size="24" />
        <div
          class="max-w-sm rounded-xl p-3 text-sm bg-n-solid-iris text-n-slate-12"
        >
          <!-- SITEUP V2 (F1.3): IA do playground gerando → Thinking Orb -->
          <ThinkingOrb state="working" :size="64" />
        </div>
      </div>
    </div>
  </div>
</template>
